import { useMemo } from "react";
import { useProjectRecord } from "@/hooks/use-record";
import { useCollectionItems } from "@/components/reports/collection-detail-view/use-collection-items";
import type { ProjectRecord } from "@/types/api";

/**
 * The record a photos report has open, or null while there is none to show.
 *
 * The report's contents already hold a copy of each record, which opens the
 * viewer at once. The record's own read replaces it as soon as it lands: it is
 * the copy every photo write keeps current (the report's contents only catch up
 * on their refetch), and it carries the full `images[]` of a multi-photo record.
 * See [[report-record-viewer.spec]] US-001.
 */
export function useReportRecord(
  projectId: string,
  collectionId: string,
  recordId: string | null,
): ProjectRecord | null {
  const { data: itemData } = useCollectionItems(projectId, collectionId);
  const { data: fetched } = useProjectRecord(projectId, recordId ?? undefined);

  return useMemo(() => {
    if (!recordId) return null;
    if (fetched?.id === recordId) return fetched;
    return (
      itemData?.items.find(
        (item) => !item.is_deleted && item.record?.id === recordId,
      )?.record ?? null
    );
  }, [recordId, fetched, itemData]);
}
